import {
  useAddCognitoGroupMutation,
  useDeleteCognitoGroupMutation,
  useGetCognitoGroupsByUniversityIdQuery,
} from "@api/university";
import { ModalVariant, useModal } from "@components/Modal";
import { useToastEffect } from "@hooks/hooks";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface CognitoGroupsManagerProps {
  selectedUniversityId: string;
}

const CognitoGroupsManager = ({
  selectedUniversityId,
}: CognitoGroupsManagerProps) => {
  const { t } = useTranslation(["universityManagement", "commons"]);
  const [newGroupId, setNewGroupId] = useState("");
  const [groupToDelete, setGroupToDelete] = useState<string | undefined>();

  const { Modal: DeleteModal, openModal: openDeleteModal } = useModal();

  const { data: cognitoGroups, refetch: refetchCognitoGroups } =
    useGetCognitoGroupsByUniversityIdQuery(selectedUniversityId);

  const [
    addCognitoGroup,
    {
      isSuccess: isSuccessAddCognitoGroup,
      isError: isErrorAddCognitoGroup,
      reset: resetAddCognitoGroup,
    },
  ] = useAddCognitoGroupMutation();

  const [
    removeCognitoGroup,
    {
      isSuccess: isSuccessDeleteCognitoGroup,
      isError: isErrorDeleteCognitoGroup,
      reset: resetDeleteCognitoGroup,
    },
  ] = useDeleteCognitoGroupMutation();

  useToastEffect({
    isSuccess: isSuccessAddCognitoGroup,
    successMessage: t("universityManagementPage.successAddCognitoGroup", {
      ns: "toast",
    }),
    isError: isErrorAddCognitoGroup,
  });

  useToastEffect({
    isSuccess: isSuccessDeleteCognitoGroup,
    successMessage: t("universityManagementPage.successDeleteCognitoGroup", {
      ns: "toast",
    }),
    isError: isErrorDeleteCognitoGroup,
  });

  const handleAddCognitoGroup = () => {
    if (!newGroupId.trim()) {
      return;
    }
    addCognitoGroup({
      universityId: selectedUniversityId,
      cognitoGroup: {
        groupId: newGroupId.trim(),
        universityId: selectedUniversityId,
      },
    });
  };

  const handleDeleteCognitoGroup = (groupId: string) => {
    setGroupToDelete(groupId);
    openDeleteModal();
  };

  const confirmDeleteCognitoGroup = () => {
    if (!groupToDelete) {
      return;
    }
    removeCognitoGroup({
      universityId: selectedUniversityId,
      groupId: groupToDelete,
    });
    setGroupToDelete(undefined);
  };

  useEffect(() => {
    if (!isSuccessAddCognitoGroup) {
      return;
    }
    resetAddCognitoGroup();
    setNewGroupId("");
    refetchCognitoGroups();
  }, [isSuccessAddCognitoGroup, resetAddCognitoGroup, refetchCognitoGroups]);

  useEffect(() => {
    if (!isSuccessDeleteCognitoGroup) {
      return;
    }
    resetDeleteCognitoGroup();
    refetchCognitoGroups();
  }, [
    isSuccessDeleteCognitoGroup,
    resetDeleteCognitoGroup,
    refetchCognitoGroups,
  ]);

  return (
    <>
      <div className="card bg-base-200 shadow-lg">
        <div className="card-body">
          <h3 className="card-title mb-4 text-lg">{t("cognitoGroups")}</h3>

          <div className="mb-4">
            <div className="flex gap-2">
              <input
                type="text"
                className="input flex-1"
                placeholder={t("groupId")}
                value={newGroupId}
                onChange={(e) => setNewGroupId(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddCognitoGroup}
                disabled={!newGroupId.trim()}
              >
                {t("addCognitoGroup")}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {cognitoGroups?.map((group) => (
              <div
                key={group.cognitoGroupId}
                className="flex items-center justify-between rounded-lg bg-base-100 p-3"
              >
                <span className="font-mono">{group.cognitoGroupId}</span>
                <button
                  type="button"
                  className="btn btn-error btn-sm"
                  onClick={() => handleDeleteCognitoGroup(group.cognitoGroupId)}
                >
                  {t("remove", { ns: "commons" })}
                </button>
              </div>
            ))}
            {(!cognitoGroups || cognitoGroups.length === 0) && (
              <div className="py-4 text-center text-gray-500">
                {t("noCognitoGroups")}
              </div>
            )}
          </div>
        </div>
      </div>

      <DeleteModal
        confirmVariant={ModalVariant.DESTRUCTIVE}
        title={t("confirmDeleteCognitoGroup")}
        onConfirm={confirmDeleteCognitoGroup}
        onConfirmLabel={t("delete", { ns: "commons" })}
        onCancelLabel={t("cancel", { ns: "commons" })}
      >
        <p>
          {t("confirmDeleteCognitoGroupMessage", {
            groupId: groupToDelete,
          })}
        </p>
      </DeleteModal>
    </>
  );
};

export default CognitoGroupsManager;
